import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../axios";

export default function InvoicePrintPos() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!invoiceId) return;
    
    // استخدام نقطة النهاية المخصصة للطباعة لضمان جلب كافة البيانات بما فيها الشركة
    axios.get(`/Invoice/print/${invoiceId}`)
      .then(response => {
        setData(response.data);
        setLoading(false);
        // طباعة تلقائية بعد تحميل البيانات
        setTimeout(() => window.print(), 500);
      })
      .catch(error => {
        console.error("Error loading invoice:", error);
        setLoading(false);
      });
  }, [invoiceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 font-bold">جاري تحميل الفاتورة...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-red-600 text-xl font-bold">لم يتم العثور على الفاتورة</p>
      </div>
    );
  }

  // تفكيك البيانات القادمة من الـ API
  const {
    invoiceNumber,
    issueDate,
    company,
    customer,
    items,
    totals,
    qrCodeUrl,
    paymentMethodName,
    invoiceStatus,
    paidAmount,
    notes
  } = data;

  const netTotal = totals?.net || 0;
  const changeDue = Math.max(0, (paidAmount || 0) - netTotal);

  return (
    <div className="print-container" dir="rtl">
      <div className="invoice-wrapper">
        {/* Header */}
        <div className="invoice-header">
          <div className="company-logo">
            {company?.logoUrl && <img src={company.logoUrl} alt={company.name} />}
          </div>
          <h1 className="company-name">{company?.name || "اسم الشركة"}</h1>
          {company?.nameEn && <h2 className="company-name-en font-bold text-sm">{company.nameEn}</h2>}
          <p className="company-info">{company?.address || ""}</p>
          <p className="company-info" dir="ltr">{company?.phone || ""}</p>
          {company?.taxNumber && (
            <p className="company-info font-bold">الرقم الضريبي: {company.taxNumber}</p>
          )}
          <div className="divider"></div>
        </div>

        {/* Invoice Info */}
        <div className="invoice-info">
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">رقم الفاتورة:</span>
              <span className="info-value font-bold">{invoiceNumber}</span>
            </div>
            <div className="info-row">
              <span className="info-label">التاريخ:</span>
              <span className="info-value">
                {new Date(issueDate).toLocaleDateString("en-CA")}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">الوقت:</span>
              <span className="info-value">
                {new Date(issueDate).toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            {customer?.name && (
              <div className="info-row full-width">
                <span className="info-label">العميل:</span>
                <span className="info-value">{customer.name}</span>
              </div>
            )}
            <div className="info-row">
               <span className="info-label">طريقة الدفع:</span>
               <span className="info-value">{paymentMethodName || '-'}</span>
            </div>
          </div>
          <div className="divider"></div>
        </div>

        {/* Items List (POS Style) */}
        <div className="invoice-items">
          <div className="items-header-row">
             <span className="font-bold">الصنف</span>
             <span className="font-bold">الإجمالي</span>
          </div>
          <div className="divider-light"></div>
          {items?.map((item, index) => {
            const qty = item.quantity ?? 0;
            const price = item.unitPrice ?? 0;
            const discount = item.discount ?? 0;
            const taxValue = item.taxValue ?? 0; // حسب المتاح في InvoicePrint.jsx
            // أو نحسبها إذا لم تكن موجودة
            // في InvoicePrint.jsx: const taxValue = item.taxValue ?? 0;
            
            // في InvoicePrint.jsx الحساب كالتالي:
            // const total = qty * price;
            // const afterDisc = total - discount;
            // const net = afterDisc + taxValue;

            // للتوافق مع POS، سنعرض الصافي النهائي للصنف
            const itemTotal = qty * price;
            const itemNet = (itemTotal - discount) + taxValue;
            
            return (
              <div key={index} className="item-row">
                <div className="item-line-1">
                  <span className="item-name font-bold">{item.productName || item.arabicName}</span>
                  <span className="item-total-price font-bold">{itemNet.toFixed(2)}</span>
                </div>
                <div className="item-line-2">
                  <span>{Number(qty).toFixed(2)} {item.unitName || ''} × {Number(price).toFixed(2)}</span>
                  {(discount > 0) && <span className="text-xs mr-2">(خصم: {Number(discount).toFixed(2)})</span>}
                  {(taxValue > 0) && <span className="text-xs mr-2">(ضريبة: {Number(taxValue).toFixed(2)})</span>}
                </div>
              </div>
            );
          })}
          <div className="divider"></div>
        </div>

        {/* Totals */}
        <div className="invoice-totals">
          <div className="total-row">
            <span className="total-label">الإجمالي (قبل الضريبة):</span>
            <span className="total-value">{totals?.subtotal?.toFixed(2) || "0.00"}</span>
          </div>
          
          {(totals?.discountTotal > 0) && (
            <div className="total-row">
              <span className="total-label">مجموع الخصم:</span>
              <span className="total-value">-{totals.discountTotal.toFixed(2)}</span>
            </div>
          )}
          
          <div className="total-row">
            <span className="total-label">ضريبة القيمة المضافة:</span>
            <span className="total-value">{totals?.totalVAT?.toFixed(2) || "0.00"}</span>
          </div>
          
          <div className="divider-bold"></div>
          
          <div className="total-row-final">
            <span className="total-label-final text-xl">الصافي النهائي:</span>
            <span className="total-value-final text-2xl">{totals?.net?.toFixed(2) || "0.00"}</span>
          </div>
          
          <div className="divider-light"></div>

          <div className="total-row">
            <span className="total-label font-bold">المبلغ المدفوع:</span>
            <span className="total-value font-bold">{(paidAmount || 0).toFixed(2)}</span>
          </div>
          
          {changeDue > 0 && (
            <div className="total-row">
              <span className="total-label font-bold">الباقي للعميل:</span>
              <span className="total-value font-bold">{changeDue.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* QR Code */}
        {qrCodeUrl && (
          <div className="qr-section">
            <img src={qrCodeUrl} alt="QR Code" className="qr-code" />
          </div>
        )}

        {/* Footer */}
        <div className="invoice-footer">
          <p className="footer-text font-bold">شكراً لزيارتكم</p>
          {company?.website && (
            <p className="footer-text">{company.website}</p>
          )}
        </div>

        {/* Print Actions */}
        <div className="no-print action-buttons">
          <button onClick={() => window.print()} className="btn-print">🖨️ طباعة</button>
          <button onClick={() => window.close()} className="btn-close">✕ إغلاق</button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Cairo', sans-serif;
        }

        body {
          background-color: #f3f4f6;
          color: #000;
        }

        .print-container {
          display: flex;
          justify-content: center;
          padding: 20px;
          min-height: 100vh;
        }

        .invoice-wrapper {
          width: 80mm;
          min-height: 200px;
          background: white;
          padding: 5mm;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        /* Typography & Layout */
        .font-bold { font-weight: 700; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-xl { font-size: 1.25rem; }
        .text-2xl { font-size: 1.5rem; }
        .text-xs { font-size: 0.75rem; }
        .mr-2 { margin-right: 0.5rem; }

        .invoice-header {
          text-align: center;
          margin-bottom: 10px;
        }
        .company-name { font-size: 16px; font-weight: 800; margin: 5px 0; }
        .company-info { font-size: 11px; margin-bottom: 2px; }
        .company-logo img { max-width: 60px; max-height: 60px; object-fit: contain; margin: 0 auto; }

        .divider { border-top: 1px dashed #000; margin: 8px 0; }
        .divider-light { border-top: 1px dotted #ccc; margin: 5px 0; }
        .divider-bold { border-top: 2px solid #000; margin: 8px 0; }

        .info-grid { display: flex; flex-direction: column; gap: 4px; }
        .info-row { display: flex; justify-content: space-between; font-size: 11px; }
        .info-row.full-width { flex-direction: column; align-items: flex-start; }
        .info-row.full-width .info-value { align-self: flex-start; }

        /* Items */
        .items-header-row { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 2px; }
        .item-row { margin-bottom: 6px; font-size: 12px; }
        .item-line-1 { display: flex; justify-content: space-between; align-items: baseline; }
        .item-line-2 { font-size: 11px; color: #444; margin-top: 1px; }
        .item-name { flex: 1; margin-left: 8px; }

        /* Totals */
        .total-row { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 3px; }
        .total-row-final { display: flex; justify-content: space-between; align-items: center; margin: 5px 0; }
        
        /* QR & Footer */
        .qr-section { text-align: center; margin: 10px 0; }
        .qr-code { width: 100px; height: 100px; margin: 0 auto; }
        .invoice-footer { text-align: center; font-size: 11px; margin-top: 10px; }

        /* Buttons */
        .action-buttons {
          display: flex; gap: 10px; justify-content: center; margin-top: 20px;
          border-top: 1px solid #eee; padding-top: 15px;
        }
        .btn-print, .btn-close {
          padding: 8px 20px; border-radius: 6px; border: none; font-weight: bold; cursor: pointer; color: white;
        }
        .btn-print { background-color: #2563eb; }
        .btn-close { background-color: #dc2626; }

        /* Print Media Query */
        @media print {
          @page { size: 80mm auto; margin: 0; }
          body { background: white; margin: 0; padding: 0; }
          .print-container { padding: 0; min-height: auto; display: block; margin: 0; }
          .invoice-wrapper { width: 80mm; box-shadow: none; padding: 2mm 5mm; margin: 0; }
          .no-print { display: none !important; }
          .item-line-2 { color: #000 !important; }
          
          /* Hide browser header/footer */
          @page { margin: 0; }
        }
      `}</style>
    </div>
  );
}
